import clsx from "clsx"
import { Command } from "cmdk"
import { useRef, useState } from "react"

import { faIcon } from "@utils/faIcon"

import { ExtraTouchArea, Skeleton } from "@particles/index"

import { Divider, Icon, IconButton } from "@atoms"

import { useSelectContext } from "@molecules/Select/Select.context"

import styles from "./SelectListSearch.module.scss"
import { SelectListSearchProps } from "./SelectListSearch.types"

export default function SelectListSearch(props: SelectListSearchProps) {
    const {
        defaultValue = "",
        isClearable = true,

        placeholder = "Search...",

        autoFocus = true,
        isDisabled = false,

        isLoading = false,
        value: controlledValue,
        onChange,
    } = props

    const { searchData } = useSelectContext()
    const [internalValue, setInternalValue] = useState<string>(defaultValue)
    const inputRef = useRef<HTMLInputElement | null>(null)

    const contextSearchState = searchData?.searchState
    const isControlled = controlledValue !== undefined
    const isContextControlled = !isControlled && contextSearchState !== undefined
    const value = isControlled ? controlledValue : isContextControlled ? contextSearchState.searchTerm : internalValue

    const handleValueChange = (newValue: string) => {
        if (isControlled) {
            onChange?.(newValue)
        } else if (isContextControlled) {
            contextSearchState.onSearchChange(newValue)
            onChange?.(newValue)
        } else {
            setInternalValue(newValue)
            onChange?.(newValue)
        }
    }

    const handleClear = () => {
        handleValueChange("")
        inputRef.current?.focus()
    }

    const showClearButton = isClearable && value.length > 0 && !isLoading

    return (
        <div
            className={clsx(styles.base, { [styles.disabled]: isDisabled, [styles.loading]: isLoading })}
            aria-busy={isLoading}
        >
            <div className={styles.inputWrap}>
                {isLoading ? (
                    <div className={styles.icon}>
                        <Skeleton circle={true} width={14} height={14} />
                    </div>
                ) : (
                    <Icon
                        icon={faIcon({ name: "magnifying-glass", style: "regular" })}
                        size={14}
                        className={styles.icon}
                    />
                )}

                {isLoading ? (
                    <Skeleton width="100%" height={10} borderRadius={0} />
                ) : (
                    <Command.Input
                        ref={inputRef}
                        value={value}
                        onValueChange={handleValueChange}
                        className={styles.input}
                        placeholder={placeholder}
                        autoFocus={autoFocus}
                        disabled={isDisabled || isLoading}
                    />
                )}

                {showClearButton && (
                    <ExtraTouchArea extraTouchArea={6}>
                        <IconButton
                            size="sm"
                            variant="ghost"
                            icon={faIcon({ name: "circle-xmark", style: "solid" })}
                            aria-label="Clear search"
                            onClick={handleClear}
                            noDebounce={true}
                            isDisabled={isDisabled}
                        />
                    </ExtraTouchArea>
                )}
            </div>
            <Divider contrast="low" />
        </div>
    )
}
